import React, { useEffect, useState } from 'react';
import { sponsorsApi, resolveImageUrl } from '../lib/api';
import { ExternalLink, MapPin, Handshake, Star } from 'lucide-react';
import { Button } from '../components/ui/button';

const TIER_LABEL = {
  presenting: 'Presenting Sponsor',
  platinum: 'Platinum',
  gold: 'Gold',
  silver: 'Silver',
  bronze: 'Bronze',
  friend: 'Friend of the Nation',
};

const TIER_ACCENT = {
  presenting: 'from-[#FF3B30] to-[#FF7A5C]',
  platinum: 'from-slate-200 to-slate-400',
  gold: 'from-yellow-400 to-amber-600',
  silver: 'from-slate-300 to-slate-500',
  bronze: 'from-orange-500 to-amber-700',
  friend: 'from-neutral-500 to-neutral-700',
};

export default function SponsorsPage() {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    sponsorsApi.list()
      .then((res) => setSponsors(res.data || []))
      .catch(() => setSponsors([]))
      .finally(() => setLoading(false));
  }, []);

  const presenting = sponsors.filter((s) => (s.tier || 'friend').toLowerCase() === 'presenting');
  const rest = sponsors.filter((s) => (s.tier || 'friend').toLowerCase() !== 'presenting');

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="sponsors-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: 'radial-gradient(circle at 30% 40%, rgba(255,59,48,.35), transparent 60%), radial-gradient(circle at 80% 20%, rgba(255,122,92,.25), transparent 55%)',
        }} />
        <div className="container relative mx-auto px-4 py-16 sm:py-24">
          <div className="flex items-center gap-3 text-xs uppercase tracking-[0.35em] text-[#FF7A5C] mb-4">
            <Handshake className="w-4 h-4" />
            <span>Proudly Supported By</span>
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight">
            OUR SPONSORS
          </h1>
          <p className="mt-6 max-w-2xl text-gray-300 text-base leading-relaxed">
            Crown Vic Nation is powered by the businesses and racers who believe in the
            grassroots short-track scene. Show them some love — they keep the wheels turning.
          </p>
        </div>
      </section>

      {loading && (
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="w-12 h-12 border-4 border-[#FF3B30] border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-gray-400 text-xs uppercase tracking-widest">Loading sponsors…</p>
        </div>
      )}

      {!loading && sponsors.length === 0 && (
        <div className="container mx-auto px-4 py-20 text-center">
          <p className="text-gray-400">No sponsors listed yet. Check back soon.</p>
        </div>
      )}

      {/* Presenting sponsors — full-width feature cards */}
      {presenting.length > 0 && (
        <section className="container mx-auto px-4 py-12 space-y-10">
          {presenting.map((s) => (
            <PresentingCard key={s.id} sponsor={s} />
          ))}
        </section>
      )}

      {/* Remaining sponsors — grid */}
      {rest.length > 0 && (
        <section className="container mx-auto px-4 pb-20">
          <h2 className="font-heading text-2xl font-bold uppercase tracking-wider text-white mb-6">
            More Partners
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((s) => <SponsorCard key={s.id} sponsor={s} />)}
          </div>
        </section>
      )}
    </div>
  );
}

function PresentingCard({ sponsor }) {
  const tier = (sponsor.tier || 'friend').toLowerCase();
  return (
    <article
      className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#141414] to-[#0A0A0A] shadow-2xl"
      data-testid={`sponsor-card-${sponsor.id}`}
    >
      <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${TIER_ACCENT[tier] || TIER_ACCENT.friend}`} />
      <div className="grid md:grid-cols-2 gap-0">
        <div className="p-6 sm:p-10 flex items-center justify-center bg-white/[0.02]">
          {sponsor.logo_url ? (
            <img
              src={resolveImageUrl(sponsor.logo_url)}
              alt={sponsor.name}
              loading="lazy"
              className="max-h-56 sm:max-h-72 w-auto object-contain drop-shadow-2xl"
              data-testid={`sponsor-logo-${sponsor.id}`}
            />
          ) : (
            <div className="w-full aspect-video bg-white/5 rounded-xl flex items-center justify-center text-gray-500 text-sm">
              No logo
            </div>
          )}
        </div>
        <div className="p-6 sm:p-10 flex flex-col justify-center">
          <div className="inline-flex items-center gap-2 self-start px-3 py-1 rounded-full bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF7A5C] text-[10px] uppercase tracking-[0.25em] font-semibold mb-4">
            <Star className="w-3 h-3" />
            {TIER_LABEL[tier] || 'Sponsor'}
          </div>
          <h3 className="font-heading font-black text-3xl sm:text-4xl leading-tight tracking-tight text-white">
            {sponsor.name}
          </h3>
          {sponsor.location && (
            <p className="mt-2 flex items-center gap-2 text-sm text-gray-400">
              <MapPin className="w-4 h-4" />
              {sponsor.location}
            </p>
          )}
          {sponsor.tagline && (
            <p className="mt-4 text-[#FF7A5C] font-medium leading-relaxed">{sponsor.tagline}</p>
          )}
          {sponsor.description && (
            <div className="mt-4 text-gray-300 text-sm leading-relaxed whitespace-pre-line">
              {sponsor.description}
            </div>
          )}
          {sponsor.website_url && (
            <a
              href={sponsor.website_url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6"
              data-testid={`sponsor-link-${sponsor.id}`}
            >
              <Button className="bg-[#FF3B30] hover:bg-[#E6352B] text-white font-semibold gap-2 rounded-full px-6">
                Visit Sponsor
                <ExternalLink className="w-4 h-4" />
              </Button>
            </a>
          )}
        </div>
      </div>
    </article>
  );
}

function SponsorCard({ sponsor }) {
  const tier = (sponsor.tier || 'friend').toLowerCase();
  return (
    <a
      href={sponsor.website_url || '#'}
      target={sponsor.website_url ? '_blank' : undefined}
      rel="noopener noreferrer"
      className="group relative block rounded-xl border border-white/10 bg-[#141414] p-6 transition-all hover:border-[#FF3B30]/40 hover:-translate-y-1"
      data-testid={`sponsor-card-${sponsor.id}`}
    >
      <div className={`absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r ${TIER_ACCENT[tier] || TIER_ACCENT.friend} rounded-t-xl`} />
      <div className="aspect-video w-full flex items-center justify-center mb-4 bg-white/[0.03] rounded-lg">
        {sponsor.logo_url ? (
          <img
            src={resolveImageUrl(sponsor.logo_url)}
            alt={sponsor.name}
            loading="lazy"
            className="max-h-32 w-auto object-contain"
          />
        ) : (
          <span className="text-gray-500 text-xs uppercase">{sponsor.name}</span>
        )}
      </div>
      <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">
        {TIER_LABEL[tier] || 'Sponsor'}
      </p>
      <h3 className="font-heading font-bold text-lg text-white group-hover:text-[#FF7A5C] transition-colors">
        {sponsor.name}
      </h3>
      {sponsor.tagline && (
        <p className="mt-2 text-sm text-gray-400 leading-relaxed line-clamp-3">{sponsor.tagline}</p>
      )}
      {sponsor.website_url && (
        <p className="mt-4 flex items-center gap-2 text-xs text-[#FF7A5C]">
          Visit <ExternalLink className="w-3 h-3" />
        </p>
      )}
    </a>
  );
}
